import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Facebook,
  Instagram,
  Twitter,
  Star,
  Heart,
} from "lucide-react";
import { formatCanadianPhoneNumber, getLogoImageUrl } from "@/services/utlits";
import { useApp } from "@/App";

export function Footer() {
  const logoUrl = getLogoImageUrl("thebiryanis.png");
  const { restaurant } = useApp();

  return (
    <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-emerald-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <img
          src="https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=1600"
          alt="Thai Pattern"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="relative z-10">
        {/* Newsletter Section */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 py-8 sm:py-12">
          <div className="max-w-7xl mx-auto mobile-padding text-center">
            <h3 className="text-2xl sm:text-3xl font-bold mb-4 font-display">
              Stay Updated with {restaurant?.name}
            </h3>
            <p className="text-emerald-100 mb-6 sm:mb-8 text-sm sm:text-base max-w-2xl mx-auto">
              Get the latest updates on new biryani flavors, special offers, and
              exclusive deals delivered straight to your inbox.
            </p>
            <div className="flex flex-col sm:flex-row max-w-md mx-auto space-y-3 sm:space-y-0 sm:space-x-3">
              <Input
                type="email"
                placeholder="Enter your email"
                className="flex-1 h-12 bg-white/10 border-white/20 text-white placeholder:text-emerald-200 focus:border-white focus:ring-white"
              />
              <Button className="h-12 bg-white text-emerald-700 hover:bg-emerald-50 font-semibold px-6">
                Subscribe
              </Button>
            </div>
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="py-12 sm:py-16">
          <div className="max-w-7xl mx-auto mobile-padding">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
              {/* Brand Section */}
              <div className="lg:col-span-1">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
                    <img
                      src={logoUrl}
                      alt="The Biryanis logo"
                      className="w-full h-full object-cover rounded-xl"
                    />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold font-display">
                      {restaurant?.name}
                    </h3>
                    <p className="text-emerald-300 text-sm">
                      Authentic Biryanis. Bold Indian Flavors.
                    </p>
                  </div>
                </div>
                <p className="text-gray-300 mb-6 text-sm leading-relaxed">
                  At The Biryanis, we bring you the soul of Indian cuisine
                  through our authentic biryanis, traditional curries, and
                  freshly prepared breads. Using time-honored recipes and
                  quality ingredients, we are proud to serve rich, flavorful
                  Indian food across Canada.
                </p>
                {/* <div className="flex items-center space-x-2 text-yellow-400 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                  <span className="text-gray-300 text-sm ml-2">
                    4.8 (2,100+ reviews)
                  </span>
                </div> */}
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="text-lg font-semibold mb-6 font-display">
                  Our Menu
                </h4>
                <ul className="space-y-3">
                  <li>
                    <Link
                      to="/?category=Popular Items"
                      className="text-gray-300 hover:text-emerald-300 transition-colors text-sm"
                    >
                      Biryanis
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/?category=Appetizers"
                      className="text-gray-300 hover:text-emerald-300 transition-colors text-sm"
                    >
                      Breads
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/?category=Curries"
                      className="text-gray-300 hover:text-emerald-300 transition-colors text-sm"
                    >
                      Curries
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/?category=Noodles"
                      className="text-gray-300 hover:text-emerald-300 transition-colors text-sm"
                    >
                      Starter & Wings
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Contact Info */}
              <div>
                <h4 className="text-lg font-semibold mb-6 font-display">
                  Contact Us
                </h4>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-gray-300 text-sm">
                        {restaurant?.address1}
                      </p>
                      <p className="text-gray-300 text-sm">
                        {restaurant?.address2}
                      </p>
                    </div>
                  </div>
                  {restaurant?.phone && (
                    <div className="flex items-center space-x-3">
                      <Phone className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <a
                        href="tel:+13105550123"
                        className="text-gray-300 hover:text-emerald-300 transition-colors text-sm"
                      >
                        {formatCanadianPhoneNumber(restaurant?.phone || "")}
                      </a>
                    </div>
                  )}
                  {restaurant?.email && (
                    <div className="flex items-center space-x-3">
                      <Mail className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <a
                        href="mailto:info@emporiumthai.com"
                        className="text-gray-300 hover:text-emerald-300 transition-colors text-sm"
                      >
                        {restaurant?.email}
                      </a>
                    </div>
                  )}
                  <div className="flex items-start space-x-3">
                    <Clock className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm">
                      <p className="text-gray-300">
                        Mon-Friday: 10:45 AM - 9:00 PM
                      </p>
                      <p className="text-gray-300">
                        Sat-Sun: 10:45 AM - 9:00 PM
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social & Services */}
              <div>
                <h4 className="text-lg font-semibold mb-6 font-display">
                  Follow Us
                </h4>
                <div className="flex space-x-4 mb-8">
                  <a
                    href="#"
                    className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-emerald-600 transition-colors"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                  <a
                    href="#"
                    className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-emerald-600 transition-colors"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                  <a
                    href="#"
                    className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-emerald-600 transition-colors"
                  >
                    <Twitter className="w-5 h-5" />
                  </a>
                </div>

                <h5 className="text-base font-semibold mb-4 font-display">
                  Services
                </h5>
                <ul className="space-y-3">
                  <li>
                    <Link
                      to="/delivery"
                      className="text-gray-300 hover:text-emerald-300 transition-colors text-sm"
                    >
                      Delivery & Pickup
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/catering"
                      className="text-gray-300 hover:text-emerald-300 transition-colors text-sm"
                    >
                      Catering Services
                    </Link>
                  </li>
                  {/* <li>
                    <Link
                      to="/gift-cards"
                      className="text-gray-300 hover:text-emerald-300 transition-colors text-sm"
                    >
                      Gift Cards
                    </Link>
                  </li> */}
                  {/* <li>
                    <Link
                      to="/loyalty"
                      className="text-gray-300 hover:text-emerald-300 transition-colors text-sm"
                    >
                      Loyalty Program
                    </Link>
                  </li> */}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <Separator className="bg-white/10" />

        {/* Bottom Bar */}
        <div className="py-6 sm:py-8">
          <div className="max-w-7xl mx-auto mobile-padding">
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
              <div className="flex items-center space-x-2 text-gray-400 text-sm">
                <span>
                  © {new Date().getFullYear()}{" "}
                  {/* <a
                    href="https://ezybite.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:underline"
                  >
                    ezybite
                  </a> */}
                  The Biryanis . All rights reserved.
                </span>
                <Heart className="w-4 h-4 text-red-400 fill-current" />
              </div>
              <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-6 text-sm">
                <div className="flex items-center space-x-4">
                  <Link
                    to="/privacy"
                    className="text-gray-400 hover:text-emerald-300 transition-colors"
                  >
                    Privacy Policy
                  </Link>
                  <Link
                    to="/terms"
                    className="text-gray-400 hover:text-emerald-300 transition-colors"
                  >
                    Terms of Service
                  </Link>
                </div>
                <div className="flex items-center space-x-2">
                  <img
                    src="https://images.pexels.com/photos/1640777/pexels-photo-1640777.jpeg?auto=compress&cs=tinysrgb&w=100"
                    alt="Thai Flag"
                    className="w-6 h-4 rounded object-cover"
                  />
                  <span className="text-emerald-300 font-medium">
                    Authentic Biryanis. Bold Indian Flavors.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
